import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { prepareLocalAccount } from '../lib/localAccount';
import { sessionCoordinator } from '../lib/verifiedSession';
import CloudAccount from './CloudAccount';
import PasswordAccess from './PasswordAccess';

export default function AuthGate({ children, client = supabase }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(client));
  const [message, setMessage] = useState('');
  const [retry, setRetry] = useState(0);
  const [recovery, setRecovery] = useState(() => new URLSearchParams(window.location.search).get('auth') === 'recovery' || new URLSearchParams(window.location.hash.slice(1)).get('type') === 'recovery');

  useEffect(() => {
    if (!client) { setLoading(false); return; }
    let alive = true; let pending; let timeout; let currentId = null;
    const coordinator = sessionCoordinator({
      client,
      prepare: prepareLocalAccount,
      accept: verified => {
        if (!alive) return;
        clearTimeout(timeout);
        currentId = verified?.id || null;
        setUser(verified); setLoading(false); setMessage('');
      },
      reject: error => { if (alive) { clearTimeout(timeout); currentId = null; setUser(null); setLoading(false); setMessage(error.message); } },
    });
    // Supabase emits INITIAL_SESSION; never await another auth call inside its callback.
    timeout = setTimeout(() => {
      if (alive && !currentId) { setLoading(false); setMessage('El acceso está tardando más de lo habitual. Comprueba tu conexión y vuelve a intentarlo.'); }
    }, 15000);
    const { data } = client.auth.onAuthStateChange((_event, session) => {
      if (_event === 'PASSWORD_RECOVERY') setRecovery(true);
      clearTimeout(pending);
      if (!session) { void coordinator.update(null); return; }
      if (currentId !== session.user?.id) { setUser(null); setLoading(true); }
      pending = setTimeout(() => { if (alive) void coordinator.update(session); }, 0);
    });
    return () => { alive = false; clearTimeout(pending); clearTimeout(timeout); coordinator.dispose(); data.subscription.unsubscribe(); };
  }, [client, retry]);

  const clearRecovery = () => {
    setRecovery(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('auth');
    window.history.replaceState({}, '', url.pathname + url.search);
  };
  const signOut = async () => {
    const { error } = await client.auth.signOut({ scope: 'local' });
    if (error) throw error;
    setUser(null); clearRecovery();
  };
  if (loading) return <div className="min-h-screen bg-black text-white grid place-items-center" role="status">Verificando tu cuenta…</div>;
  if (user && recovery) return <PasswordAccess client={client} recovery onPasswordSaved={clearRecovery} onCancelRecovery={() => { signOut().catch(() => setMessage('No se pudo cerrar la sesión.')); }} />;
  if (user) return <CloudAccount key={user.id} user={user}>{children(user, signOut)}</CloudAccount>;
  return <><PasswordAccess client={client} />{message && <div role="status" className="bg-black text-white text-center p-4">{message}<button type="button" className="block mx-auto underline" onClick={() => { setLoading(true); setRetry(v => v + 1); }}>Volver a comprobar mi sesión</button></div>}</>;
}
